<template>
  <div>
    <!-- Invoices table -->
    <b-row style="margin-top: 2em">
      <b-col>
        <b-card bg-variant="light">
          <b-row>
            <b-col>
              <h5 class="text-info">{{ $t("globals.invoices") }}:</h5>
              <b-table
                small bordered hover
                primary-key="id"
                :items="invoices"
                :sort-by.sync="invoicesTable.sortBy"
                :sort-desc.sync="invoicesTable.sortDesc"
                :per-page="invoicesTable.perPage"
                :current-page="invoicesTable.currentPage"
                :fields="invoicesTable.fields"
                @row-clicked="toInvoice"
              >
                <template v-slot:cell(total_price)="item">
                  {{ currencyFormat(item.item.total_price, currency) }}
                </template>
                <template v-slot:cell(is_paid)="item">
                  <v-icon v-if="item.item.is_paid" name="regular/check-square" ></v-icon>
                  <v-icon v-else name="regular/square"></v-icon>
                </template>
                <template v-slot:cell(created_at)="item">
                  <div v-b-tooltip.hover :title="item.item.created_at">
                    {{ item.item.created_at.substring(0, 10) }}
                  </div>
                </template>
                <!-- Invoice no -->
                <template v-slot:cell(invoice_no)="item">
                  <a :href="`${getBackendUrl}/pdf_invoices/${item.item.id}.pdf?tmpl=0&locale=${getLocale}`" target="_blank">
                    {{ item.item.invoice_no }}
                  </a>
                </template>
                <template v-slot:cell(sent_at)="item">
                  <div
                    v-b-tooltip.hover
                    :title="item.item.sent_at ? item.item.sent_at : ''"
                  >
                    {{
                      item.item.sent_at
                        ? item.item.sent_at.substring(0, 10)
                        : ""
                    }}
                  </div>
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6" class="my-1">
              <b-pagination
                class="my-0"
                v-model="invoicesTable.currentPage"
                :total-rows="invoices.length"
                :per-page="invoicesTable.perPage"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <!-- Scheduled invoices table -->
    <b-row style="margin-top: 2em; margin-bottom: 2em">
      <b-col>
        <b-card bg-variant="light">
          <b-row>
            <b-col>
              <h5 class="text-info">{{ $t("globals.scheduled_invoices") }}:</h5>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-table
                small
                bordered
                primary-key="id"
                :items="invoiceSchedules"
                :sort-by.sync="invoiceSchedulesTable.sortBy"
                :sort-desc.sync="invoiceSchedulesTable.sortDesc"
                :per-page="invoiceSchedulesTable.perPage"
                :current-page="invoiceSchedulesTable.currentPage"
                :fields="invoiceSchedulesTable.fields"
              >
                <template v-slot:cell(actions)="row">
                  <b-btn
                    v-if="
                      row.item.status == 'scheduled' ||
                        row.item.status == 'scheduled_plus'
                    "
                    variant="warning"
                    style="margin-left: 3px;"
                    size="sm"
                    @click="pauseScheduledInvoice(row.item.id)"
                  >
                    <v-icon name="pause"></v-icon>
                  </b-btn>
                  <b-btn
                    v-if="
                      row.item.status == 'paused' ||
                        row.item.status == 'paused_plus'
                    "
                    variant="success"
                    style="margin-left: 3px;"
                    size="sm"
                    @click="continueScheduledInvoice(row.item.id)"
                  >
                    <v-icon name="play"></v-icon>
                  </b-btn>
                </template>
                <template v-slot:cell(total_price)="row">
                  {{ currencyFormat(row.item.total_price, currency) }}
                </template>
                <template v-slot:cell(schedule_at)="row">
                  {{ row.item.schedule_at.substring(0, 10) }}
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6" class="my-1">
              <b-pagination
                class="my-0"
                v-model="invoiceSchedulesTable.currentPage"
                :total-rows="invoiceSchedules.length"
                :per-page="invoiceSchedulesTable.perPage"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapActions } from "vuex";

export default {
  props: {
    refreshOrder: Function,
    currency: String,
    invoices: Array,
    invoiceSchedules: Array
  },
  data() {
    return {
      invoicesTable: {
        sortBy: "sent_at",
        sortDesc: true,
        perPage: 5,
        currentPage: 1,
        fields: [
          {
            key: "id",
            sortable: true
          },
          {
            key: "invoice_no",
            label: this.$t("globals.invoice_no")
          },
          {
            key: "is_paid",
            label: this.$t("globals.paid"),
            sortable: true
          },
          {
            key: "created_at",
            label: this.$t("globals.created_at"),
            sortable: true
          },
          {
            key: "sent_at",
            label: this.$t("globals.sent_at"),
            sortable: true
          },
          {
            key: "sent_to",
            label: this.$t("globals.sent_to")
          },
          {
            key: "total_price",
            label: this.$t("globals.total_price")
          }
        ]
      },
      invoiceSchedulesTable: {
        sortBy: "schedule_at",
        sortDesc: false,
        perPage: 5,
        currentPage: 1,
        fields: [
          {
            key: "id",
            sortable: true
          },
          {
            key: "status",
            label: this.$t("globals.status"),
            sortable: true
          },
          {
            key: "total_price",
            label: this.$t("globals.total_price"),
            sortable: true
          },
          {
            key: "schedule_at",
            label: this.$t("globals.schedule_at"),
            sortable: true
          },
          {
            key: "actions",
            label: this.$t("globals.actions")
          }
        ]
      }
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    currencyFormat,
    toInvoice(item) {
      this.$router.push({ name: "invoice", params: { id: item.id } });
    },
    pauseScheduledInvoice(id) {
      let data = {
        query: `
          mutation { 
            res: InvoiceScheduledPause (
              invoice_id: ${parseInt(id)}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refreshOrder();
      });
    },
    continueScheduledInvoice(id) {
      let data = {
        query: `
          mutation { 
            res: InvoiceScheduledContinue (
              invoice_id: ${parseInt(id)}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refreshOrder();
      });
    }
  },
  computed: {
    ...mapGetters(["getLocale", "getBackendUrl"])
  }
};
</script>
